import React, { forwardRef } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { CSVLink } from 'react-csv'

import { Select, Input, EntitySelect } from '@st-operations/uikit/src/v2/index.js'

import { useEventFeedsContext } from '../../../context/index.jsx'
import FileInput from '../modal-add-filters/file-input/index.jsx'
import MultiValueInput from '_components/multi-value-input/index.jsx'
import KeywordsInputWithRecommendations from '_components/keywords-input-with-recommendations/index.jsx'

import CATEGORIES_OPTIONS_RAW from '../../../categories/categories-options.json'
import AudiencesSelect from '../audiences-select/index.jsx'
import {
  FILE_OPERATORS,
  MULTI_VALUE_OPERATORS,
  SIMILAR_ATTRIBUTES,
  SIMILAR_OPERATORS,
} from '../useFiltersCRUD.js'

import './styles.scss'

const CATEGORIES_OPTIONS = CATEGORIES_OPTIONS_RAW.map((co) => ({
  label: co
    .replaceAll('_', ' ')
    .replaceAll('/', ' / ')
    .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
    .replaceAll(' / ', '/'),
  value: co,
}))

const FilterEditForm = forwardRef((props, ref) => {
  const { id, attribute, operator, value, hasHeader, onEdit, className } = props
  const {
    id: feedId,
    schema: {
      // eslint-disable-next-line no-unused-vars
      filters: { type = null, default: defaultKey = null, ...filters },
    },
  } = useEventFeedsContext()

  const types = Object.keys(filters)
    .map((type) => ({ value: type, label: filters[type].label }))
    // Audience filter is only available for discovery mode
    .filter((type) => (feedId === 'discovery' ? true : type.value !== 'audience'))

  const valueSchema = filters[attribute]?.values

  const handleEditOptionsType = (_obj, name, value) => {
    handleEdit(value, name)
  }

  const handleEdit = (value, name) => {
    onEdit(id, name, value)
  }

  const handleEditSelect = (_, _2, value) => {
    onEdit(id, 'value', value)
  }

  const handleAddFile = (url, options) => {
    onEdit(id, 'value', url, options)
  }

  const handleHasHeaderRowChange = (hasHeaderRow) => {
    onEdit(id, 'hasHeader', hasHeaderRow)
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const rawText = e.clipboardData.getData('text/plain') || ''
    const pastedValues = rawText.split(/\r?\n/g)
    const inSchemaAndNotPresent = pastedValues
      .map((pastedItem) =>
        valueSchema.find((schemaOption) => {
          pastedItem = pastedItem.trim()
          const schemaValue = extractSchemaValue(schemaOption)
          const schemaLabel = extractSchemaLabel(schemaOption)
          const valid = schemaValue === pastedItem || schemaLabel === pastedItem
          const present = !!value.find(
            (item) =>
              extractSchemaValue(item) === schemaValue || extractSchemaLabel(item) === schemaLabel
          )
          return valid && !present
        })
      )
      .filter(Boolean)

    onEdit(id, 'value', [...value, ...inSchemaAndNotPresent])
  }

  return (
    <div ref={ref} className={classnames('filter-edit-form', className)}>
      <Select
        label="Type"
        name="key"
        value={attribute}
        options={types}
        onChange={handleEditOptionsType}
        className="filter-edit-form__select"
        useReactSelect
        menuPosition="fixed"
        styles={{
          // eslint-disable-next-line no-unused-vars
          menuPortal: ({ left, top, ...provided }) => ({
            ...provided,
          }),
        }}
      />
      <Select
        label="Operator"
        name="operator"
        disabled={!attribute}
        value={operator}
        options={filters[attribute]?.operators || []}
        onChange={(_, name, value) => handleEdit(value, name)}
        className="filter-edit-form__select"
        pretty={true}
        useReactSelect
        menuPosition="fixed"
        styles={{
          // eslint-disable-next-line no-unused-vars
          menuPortal: ({ left, top, ...provided }) => ({
            ...provided,
          }),
        }}
      />
      <Choose>
        <When condition={filters[attribute]?.type === 'ref'}>
          <EntitySelect
            key={`select-${attribute}-${operator}`}
            name="value"
            label="Value"
            className="filter-edit-form__select"
            onChange={handleEditSelect}
            isMulti={filters[attribute].multi}
            value={value}
            entity={filters[attribute].collection}
            fields={['name', 'company.name', 'products', 'description']}
            menuPosition="fixed"
            styles={{
              // eslint-disable-next-line no-unused-vars
              menuPortal: ({ left, top, ...provided }) => ({
                ...provided,
              }),
            }}
          />
        </When>
        <When condition={filters[attribute]?.type === 'integer'}>
          <Input
            key={`integer-input-${attribute}-${operator}`}
            name="value"
            label="Value"
            value={value}
            onChange={handleEdit}
            className="filter-edit-form__input"
            type="number"
          />
        </When>
        <When condition={filters[attribute]?.label === 'Audience'}>
          <If condition={feedId === 'discovery'}>
            <AudiencesSelect
              key={`select-${attribute}-${operator}`}
              name="value"
              label="Value"
              className="filter-edit-form__select"
              onChange={handleEditSelect}
              isSearchable
              isClearable
              value={value}
              menuPosition="fixed"
              styles={{
                // eslint-disable-next-line no-unused-vars
                menuPortal: ({ left, top, ...provided }) => ({
                  ...provided,
                }),
              }}
            />
          </If>
        </When>
        <When condition={Array.isArray(valueSchema) && MULTI_VALUE_OPERATORS.includes(operator)}>
          <div
            style={{ height: '100%', width: '100%' }}
            onPaste={(e) => handlePaste(e, valueSchema)}
          >
            <Select
              key={`select-${attribute}-${operator}`}
              name="value"
              className="filter-edit-form__select"
              label="Value"
              isMulti={!(operator === 'equals')}
              isSearchable
              isClearable
              value={value}
              options={valueSchema}
              onChange={handleEditSelect}
              useReactSelect
              menuPosition="fixed"
              styles={{
                // eslint-disable-next-line no-unused-vars
                menuPortal: ({ left, top, ...provided }) => ({
                  ...provided,
                }),
              }}
            />
          </div>
          <If condition={valueSchema.length > 10}>
            <div className="text-gray-500 text-[12px] -mt-4 text-left">
              <div>
                This field accepts content pasted from the clipboard, formatted as newline-separated
                values or labels:
              </div>
              <div className="text-[12px]">
                e.g,{' '}
                {valueSchema
                  .slice(0, 3)
                  .map((item) => {
                    const value = extractSchemaValue(item)
                    const label = extractSchemaLabel(item)
                    if (value === label) return value
                    return `"${value}" or "${label}"`
                  })
                  .join(', ')}
              </div>
              <CSVLink
                data={valueSchema?.map((item) =>
                  typeof item === 'object' ? item : { value: item }
                )}
                filename={`de-filter-${attribute}-${dayjs(new Date()).format('YYYY-MM-DD')}.csv`}
              >
                Download Options List as CSV
              </CSVLink>
            </div>
          </If>
        </When>
        <When condition={FILE_OPERATORS.includes(operator)}>
          <FileInput
            label="Value"
            headerValue={attribute}
            onAddFile={handleAddFile}
            onHasHeaderRowChange={handleHasHeaderRowChange}
            fileName={value}
            hasHeaderRow={hasHeader}
          />
        </When>
        <When
          condition={SIMILAR_ATTRIBUTES.includes(attribute) && SIMILAR_OPERATORS.includes(operator)}
        >
          <KeywordsInputWithRecommendations
            label="Values"
            values={value || []}
            onChange={(value) => onEdit(id, 'value', value)}
          />
        </When>
        <When condition={MULTI_VALUE_OPERATORS.includes(operator)}>
          <MultiValueInput
            key={`multi-value-input-${attribute}-${operator}`}
            name="value"
            className="filter-edit-form__multi-value-input"
            label="Value"
            values={Array.isArray(value) ? value : !value ? [] : [value]}
            onChange={handleEdit}
            options={
              attribute === 'categories'
                ? CATEGORIES_OPTIONS.filter((f) => !value.includes(f))
                : undefined
            }
          />
        </When>
        <When condition={typeof valueSchema === 'string' || operator === 'equals'}>
          <Input
            key={`input-${attribute}-${operator}`}
            name="value"
            label="Value"
            value={value}
            onChange={handleEdit}
            className="filter-edit-form__input"
          />
        </When>
        <Otherwise>{null}</Otherwise>
      </Choose>
    </div>
  )
})

function extractSchemaValue(schemaValue) {
  return schemaValue.value || schemaValue.code || `${schemaValue}`
}

function extractSchemaLabel(schemaLabel) {
  return schemaLabel.label || schemaLabel.name || `${schemaLabel}`
}

FilterEditForm.propTypes = {
  attribute: PropTypes.string,
  operator: PropTypes.string,
  hasHeader: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.array, PropTypes.number]),
  id: PropTypes.number,
  onEdit: PropTypes.func,
  className: PropTypes.string,
}

FilterEditForm.defaultProps = {
  attribute: '',
  operator: '',
  value: '',
  id: 0,
  onEdit: () => {},
  className: '',
}

FilterEditForm.displayName = 'FilterEditForm'

export default React.memo(FilterEditForm)
